/**
 * Jeton média des images du brief (pages, images du moodboard extraites, vignettes du storyboard).
 *
 * Ces trois routes refusent toute requête sans session ; un <img> n'envoie pas l'en-tête Authorization, donc
 * `resolveMediaUrl` ajoute `t=<jeton média>` à leurs URL (affichage seulement). Le jeton (6 h, GET /api/media/token)
 * ne vaut que pour ces images : jamais un jeton de connexion.
 *
 * Une URL résolue finit parfois en base (historique d'images, glisser-déposer, source Toolbox) : `stripMediaTokens`
 * retire le jeton de tout corps et de tous paramètres de requête axios (intercepteur posé par App.js), et
 * `resolveMediaUrl` reconstruit ces URL depuis leur chemin nu, avec le jeton courant (un vieux jeton ne casse rien).
 */
import axios from "axios";

const BRIEF_MEDIA_RE = /\/api\/media\/(?:brief_pages|moodboard|brief_vignettes)\//;
// Jeu de caractères d'un JWT (base64url + « . ») éventuellement encodé : jamais le texte qui suit une URL collée
const TOKEN_PARAM_RE = /([?&])t=[A-Za-z0-9._~%-]*(&)?/;
const RENEW_BEFORE_MS = 2 * 3600 * 1000;    // renouvelé quand il reste moins de 2 h
const RETRY_MS = 5 * 60 * 1000;

let current = null;          // { token, expiresAt (ms) }
let timer = null;
let inFlight = null;
let epoch = 0;               // +1 à chaque déconnexion

export const isBriefMediaUrl = (url) => typeof url === "string" && BRIEF_MEDIA_RE.test(url);

export const currentMediaToken = () => (current && current.expiresAt > Date.now() ? current.token : null);

// Une URL d'image du brief dans une chaîne (texte collé compris) : chemin, puis sa requête éventuelle
const BRIEF_URL_IN_TEXT_RE = /(\/api\/media\/(?:brief_pages|moodboard|brief_vignettes)\/[^\s?#"'<>]*)(\?[^\s#"'<>]*)?/g;

/** Retire `t=` des URL d'images du brief d'une chaîne ; le reste (autres liens, texte) revient tel quel. */
export function stripMediaToken(url) {
  if (!isBriefMediaUrl(url) || !url.includes("t=")) return url;
  return url.replace(BRIEF_URL_IN_TEXT_RE, (_, path, query) => {
    let q = query || "";
    while (TOKEN_PARAM_RE.test(q)) q = q.replace(TOKEN_PARAM_RE, (__, sep, amp) => (amp ? sep : ""));
    return path + q;
  });
}

/** Copie de `value` sans jeton média dans ses chaînes ; même objet si rien à retirer (pas de copie inutile). */
export function stripMediaTokens(value, depth = 0) {
  if (typeof value === "string") return stripMediaToken(value);
  if (depth > 12 || value === null || typeof value !== "object") return value;
  if (typeof FormData !== "undefined" && value instanceof FormData) return value;
  if (typeof Blob !== "undefined" && value instanceof Blob) return value;
  if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) return value;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((v) => {
      const s = stripMediaTokens(v, depth + 1);
      if (s !== v) changed = true;
      return s;
    });
    return changed ? out : value;
  }
  if (Object.getPrototypeOf(value) !== Object.prototype) return value;
  let changed = false;
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    const s = stripMediaTokens(v, depth + 1);
    if (s !== v) changed = true;
    out[k] = s;
  }
  return changed ? out : value;
}

/** Intercepteur de requête axios : aucun jeton média ne part vers le serveur comme donnée. */
export function stripMediaTokensFromRequest(config) {
  if (config.data !== undefined) config.data = stripMediaTokens(config.data);
  if (config.params !== undefined) config.params = stripMediaTokens(config.params);
  return config;
}

// Renouvellement plus tard : l'en-tête par défaut d'axios, qui suit les rafraîchissements de la session SSO
function schedule(api, wait) {
  clearTimeout(timer);
  timer = setTimeout(() => { refreshMediaToken(api).catch(() => {}); }, wait);
}

/** Demande un jeton pour la session `login` (jeton de connexion ; absent = en-tête par défaut d'axios), puis programme
 * son renouvellement. Une seule demande à la fois. */
export function refreshMediaToken(api, login) {
  if (!inFlight) {
    const mine = epoch;       // une déconnexion pendant la demande : la réponse est jetée, rien n'est reprogrammé
    const opts = { timeout: 15000 };   // une API figée ne bloque pas les pages (elles attendent ce jeton)
    if (login) opts.headers = { Authorization: `Bearer ${login}` };
    const req = axios.get(`${api}/media/token`, opts)
      .then((res) => {
        if (mine !== epoch) return null;
        const expiresAt = Date.parse(res.data.expires_at);
        current = { token: res.data.token, expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + 3600 * 1000 };
        schedule(api, Math.max(60 * 1000, current.expiresAt - Date.now() - RENEW_BEFORE_MS));
        return current.token;
      })
      .catch((e) => {
        if (mine !== epoch) return null;
        console.warn("[media] jeton média indisponible, nouvel essai dans 5 min", e?.response?.status || e?.message);
        schedule(api, RETRY_MS);
        throw e;
      })
      .finally(() => { if (inFlight === req) inFlight = null; });
    inFlight = req;
  }
  return inFlight;
}

/** Déconnexion : plus de jeton, plus de renouvellement, demande en cours ignorée. */
export function clearMediaToken() {
  epoch += 1;
  clearTimeout(timer);
  timer = null;
  inFlight = null;
  current = null;
}
